/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import {KTIcon} from '../../../../../../_metronic/helpers'
import {deactivateAccount, IDeactivateAccount} from '../SettingsModel'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {parseId} from '../../../../../tfi/helpers'
import {TOGGLE_USER_STATUS} from '../../../../../tfi/gql/mutations/userMutations'
import {useGetUser} from '../../../../../tfi/store/userStore'

const deactivateAccountSchema = Yup.object().shape({
  confirm: Yup.boolean().oneOf([true], 'Please check the box to deactivate your account'),
})

type DeactivateAccountProps = {
  userId?: string | number
  isActive?: boolean
  onStatusChanged?: () => void
}

const DeactivateAccount: React.FC<DeactivateAccountProps> = ({
  userId,
  isActive: initialActive = true,
  onStatusChanged,
}) => {
  const currentUserId = useGetUser((s) => s.user?.user?.id)
  const logout = useGetUser((s) => s.logout)
  const [toggleUserStatus, {loading}] = useMutation(TOGGLE_USER_STATUS)
  const [isActive, setIsActive] = React.useState<boolean>(initialActive)

  React.useEffect(() => {
    setIsActive(initialActive)
  }, [initialActive])

  const formik = useFormik<IDeactivateAccount>({
    initialValues: {
      ...deactivateAccount,
    },
    validationSchema: deactivateAccountSchema,
    onSubmit: async (_values, {resetForm}) => {
      const resolvedId = userId ?? currentUserId
      const parsedId = parseInt(String(parseId(resolvedId ?? '')))
      const nextStatus = !isActive

      if (!parsedId) {
        toast.error('Unable to determine which account to update')
        return
      }

      try {
        const {data} = await toggleUserStatus({
          variables: {
            id: parsedId,
            isActive: nextStatus,
          },
        })

        if (data?.activateUserMutation?.user?.id) {
          setIsActive(nextStatus)
          toast.success(
            nextStatus
              ? 'Account has been successfully activated'
              : 'Account has been successfully deactivated'
          )
          resetForm()
          onStatusChanged?.()

          const currentParsedId = parseInt(String(parseId(currentUserId ?? '')))
          if (!nextStatus && currentParsedId === parsedId) {
            logout()
          }
        } else {
          toast.error('Unable to update account status')
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        toast.error(`Error updating account status: ${message}`)
      }
    },
  })

  return (
    <div className='card'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_deactivate'
        aria-expanded='true'
        aria-controls='kt_account_deactivate'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>{isActive ? 'Deactivate Account' : 'Activate Account'}</h3>
        </div>
      </div>

      <div id='kt_account_deactivate' className='collapse show'>
        <form onSubmit={formik.handleSubmit} id='kt_account_deactivate_form' className='form'>
          <div className='card-body border-top p-9'>
            <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed mb-9 p-6'>
              <KTIcon iconName='information-5' className='fs-2tx text-warning me-4' />

              <div className='d-flex flex-stack flex-grow-1'>
                <div className='fw-bold'>
                  <h4 className='text-gray-800 fw-bolder'>
                    {isActive ? 'You Are Deactivating Your Account' : 'You Are Activating Your Account'}
                  </h4>
                  <div className='fs-6 text-gray-600'>
                    For extra security, this requires you to confirm your email or phone number when
                    you reset yousignr password.
                    <br />
                    <a className='fw-bolder' href='#'>
                      Learn more
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className='form-check form-check-solid fv-row'>
              <input
                className='form-check-input'
                type='checkbox'
                {...formik.getFieldProps('confirm')}
              />
              <label className='form-check-label fw-bold ps-2 fs-6' htmlFor='deactivate'>
                {isActive ? 'I confirm my account deactivation' : 'I confirm my account activation'}
              </label>
            </div>
            {formik.touched.confirm && formik.errors.confirm && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>{formik.errors.confirm}</div>
              </div>
            )}
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button
              id='kt_account_deactivate_account_submit'
              type='submit'
              className='btn btn-danger fw-bold'
            >
              {!loading && (isActive ? 'Deactivate Account' : 'Activate Account')}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export {DeactivateAccount}
